import {generateGuid} from '@packages/utilities/guid';
import {
  formatPrice,
  sumPrice,
  type FormattedPrice,
  type CurrencySettings,
} from '@packages/systems/core/utils/CurrencyUtils';
import random from 'lodash/random';
import sample from 'lodash/sample';

export type UserItem = {
  count: number;
  price: FormattedPrice;
  rowTotal: FormattedPrice;
  product: {
    f_ec_product_type_2dr10dr: {
      name: string;
    };
    f_description_: string;
    f__archived_0ht: boolean;
    f__draft_0ht: boolean;
    f_name_: string;
    f_sku_properties_3dr: Record<any, any> | null;
    id: string;
  };
  sku: {
    id: string;
    f_compare_at_price_7dr10dr: FormattedPrice;
    ecSkuInventoryQuantity: number;
    f_width_: number;
    f_length_: number;
    f_height_: number;
    f_weight_: number;
    f__archived_0ht: boolean;
    f__draft_0ht: boolean;
    f_main_image_4dr: {
      url: string;
    };
    f_sku_values_3dr: Array<Record<any, any>>;
  };
};

const getRandomLoremIpsum = () => {
  return sample([
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    'Maecenas euismod sapien eu arcu convallis, vitae vestibulum ipsum maximus.',
    'Fusce aliquet turpis at orci bibendum, non convallis justo tempor.',
    'Vivamus vitae arcu vel velit efficitur vestibulum vel in purus.',
    'Sed auctor augue id tellus lacinia, nec ultricies est fermentum.',
    'Integer rutrum ante et nunc venenatis, id ultricies risus ultricies.',
    'Cras sit amet velit id nulla tempus dictum sit amet eu nisi.',
    'Donec eu felis at libero consequat sagittis a et urna.',
    'Praesent nec orci at nulla consequat congue ut non arcu.',
    'Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.',
  ]) as string;
};

export function generateMockUserItems({
  currencySettings,
  count,
}: {
  currencySettings: CurrencySettings;
  count: number;
}): Array<UserItem> {
  const mockItems: Array<UserItem> = [];
  for (let i = 0; i < count; i++) {
    const price = random(100, 99999);
    const formattedPrice = formatPrice({
      value: price,
      unit: currencySettings.currencyCode,
    });
    const item = {
      count: 1,
      price: {
        ...formattedPrice,
      },
      rowTotal: {
        ...formattedPrice,
      },
      product: {
        f_ec_product_type_2dr10dr: {
          name: 'Physical',
        },
        f_description_: getRandomLoremIpsum(),
        f__archived_0ht: false,
        f__draft_0ht: false,
        f_name_: `Item ${i + 1}`,
        f_sku_properties_3dr: null,
        id: generateGuid(),
      },
      sku: {
        id: generateGuid(),
        f_compare_at_price_7dr10dr: {
          ...formatPrice({
            value: price * 1.3,
            unit: currencySettings.currencyCode,
          }),
        },
        ecSkuInventoryQuantity: random(1, 100),
        f_width_: random(1, 100),
        f_length_: random(1, 100),
        f_height_: random(1, 100),
        f_weight_: random(1, 100),
        f__archived_0ht: false,
        f__draft_0ht: false,
        f_main_image_4dr: {
          url: 'https://d3e54v103j8qbb.cloudfront.net/img/image-placeholder.svg',
        },
        f_sku_values_3dr: [],
      },
    } as const;
    // @ts-expect-error - TS2345 - Argument of type '{ readonly count: 1; readonly price: { readonly value: number; readonly unit: string; readonly string: string; }; readonly rowTotal: { readonly value: number; readonly unit: string; readonly string: string; }; readonly product: { ...; }; readonly sku: { ...; }; }' is not assignable to parameter of type 'UserItem'.
    mockItems.push(item);
  }

  return mockItems;
}

export function calculateSubtotal({
  userItems,
  currencySettings,
}: {
  userItems: Array<UserItem>;
  currencySettings: CurrencySettings;
}): FormattedPrice {
  return formatPrice(
    userItems.reduce(
      (subtotal, item) => {
        return sumPrice(subtotal, item.rowTotal);
      },
      {value: 0, unit: currencySettings.currencyCode}
    )
  );
}

export function calculateTotal({
  extraItems,
  subtotal,
  currencySettings,
}: {
  extraItems: Array<{
    price: FormattedPrice;
  }>;
  subtotal: FormattedPrice;
  currencySettings: CurrencySettings;
}): FormattedPrice {
  const extraItemsTotal = extraItems.reduce(
    (extraTotal, item) => sumPrice(extraTotal, item.price),
    {value: 0, unit: currencySettings.currencyCode}
  );
  return formatPrice(sumPrice(subtotal, extraItemsTotal));
}

export function getPaddedUserItemsAndTotals(
  previewItemsCount: number,
  userItems: Array<UserItem>,
  extraItems: Array<{
    price: FormattedPrice;
  }>,
  currencySettings: CurrencySettings
): {
  paddedUserItems: Array<UserItem>;
  subtotal: FormattedPrice;
  total: FormattedPrice;
} {
  let paddedUserItems = userItems.slice(0, previewItemsCount) || [];
  if (previewItemsCount > userItems.length) {
    paddedUserItems = paddedUserItems.concat(
      generateMockUserItems({
        currencySettings,
        count: previewItemsCount - userItems.length,
      })
    );
  }
  const subtotal = calculateSubtotal({
    currencySettings,
    userItems: paddedUserItems,
  });
  return {
    paddedUserItems,
    subtotal,
    total: calculateTotal({currencySettings, extraItems, subtotal}),
  };
}
